// Belge Dizaynı düzenini yazdırılabilir HTML'e çevirir ve tarayıcı yazdırma diyaloğunu açar.
// PC lazer/nokta vuruşlu yazıcılar OS sürücüsünden geçer; CSS mm birimi WYSIWYG sağlar.
import { paperDims, SOURCE_FIELDS } from './fieldCatalog';
import { barcodeHtml } from './barcode';
import { computeAggregate, moneyToTurkishWords, AGG_SHORT } from './calc';

const moneyFmt = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function formatValue(value, type) {
  if (value === null || value === undefined || value === '') return '';
  if (type === 'money') {
    const n = Number(value);
    return Number.isFinite(n) ? moneyFmt.format(n) : String(value);
  }
  if (type === 'date') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('tr-TR');
  }
  if (type === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : String(value);
  }
  return String(value);
}

// Detay (InvoiceDetail) alanının okunabilir etiketi — calc placeholder'ı için.
function detailFieldLabel(key) {
  return (SOURCE_FIELDS.InvoiceDetail?.fields.find((f) => f.key === key) || {}).label || key;
}

// Tek bir elemanın gösterilecek değerini çözer (önizleme verisi yoksa alan etiketini döndürür).
export function resolveElementValue(el, data, fallbackLabel) {
  if (el.type === 'static-text') return el.text || '';
  if (el.type === 'line' || el.type === 'box') return '';
  if (el.type === 'calc') {
    const fn = el.aggFn || 'sum';
    const rows = (data && Array.isArray(data.detail)) ? data.detail : null;
    if (rows) {
      const v = computeAggregate(rows, el.aggField, fn);
      if (v !== null && v !== undefined) return formatValue(v, el.valueType || (fn === 'count' ? 'number' : 'money'));
      return '';
    }
    // Tasarım modu (veri yok): fonksiyon + alan placeholder'ı.
    return `[${AGG_SHORT[fn] || fn} ${detailFieldLabel(el.aggField)}]`;
  }
  // Gerçek belge verisine bağlı (baskı/önizleme) mod: data.sources dolu gelir. Tasarım/boş
  // önizlemede data=null'dır → alan etiketleri placeholder olarak gösterilir.
  const boundMode = !!(data && data.sources);
  if (el.type === 'amount-words') {
    let raw;
    if (boundMode && data.sources[el.source]) raw = data.sources[el.source][el.fieldKey];
    if (raw !== undefined && raw !== null && raw !== '') {
      return moneyToTurkishWords(raw, el.currency || 'TL', el.subunit || 'Kr', !!el.upper);
    }
    // #285 Gerçek veri modunda bağlı tutar boşsa → boş (örnek tutar sızdırma yok).
    if (boundMode) return '';
    // Tasarım modu: örnek tutarı yazıyla göster.
    return moneyToTurkishWords(1250.5, el.currency || 'TL', el.subunit || 'Kr', !!el.upper);
  }
  if (el.type === 'field') {
    if (boundMode) {
      const src = data.sources[el.source];
      const raw = src ? src[el.fieldKey] : undefined;
      if (raw !== undefined && raw !== null && raw !== '') return formatValue(raw, el.fieldType || el.format);
      // #285 Gerçek veri modunda boş alan → BOŞ çıktı (tasarım placeholder'ı "[Alan]" değil).
      return '';
    }
    // Tasarım/boş önizleme modu: alanın etiketini placeholder olarak göster.
    return fallbackLabel ? `[${fallbackLabel}]` : `[${el.source}.${el.fieldKey}]`;
  }
  if (el.type === 'image') return '';
  return '';
}

// Barkod/QR elemanının kodlanacak değerini çözer.
export function resolveBarcodeValue(el, data) {
  if (el.valueMode === 'field' && el.source && el.fieldKey) {
    const raw = data?.sources?.[el.source]?.[el.fieldKey];
    if (raw !== undefined && raw !== null && raw !== '') return String(raw);
    // Tasarım modunda (veri yok) alan placeholder'ı yerine örnek değer üret.
    return el.text || '1234567890';
  }
  return el.text || '1234567890';
}

function escapeHtml(str) {
  return String(str)
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function elementStyle(el, top = el.y) {
  // #285 amount-words tasarımcıda daima sarılır; diğerlerinde `wrap` bayrağı belirler.
  const wrap = !!el.wrap || el.type === 'amount-words';
  const parts = [
    `position:absolute`,
    `left:${el.x}mm`, `top:${top}mm`,
    // #285 SABİT yükseklik (min-height DEĞİL): uzun metin (ör. müşteri ünvanı) kutuyu
    // büyütüp altındaki mutlak konumlu alanlarla çakışıyordu. Tasarımcı kutusu da sabit
    // yüksekliktedir → baskı WYSIWYG olmalı; taşan içerik overflow:hidden ile kırpılır.
    `width:${el.width}mm`, `height:${el.height}mm`,
    `font-size:${el.fontSizePt || 10}pt`,
    `font-weight:${el.bold ? 'bold' : 'normal'}`,
    `text-decoration:${el.underline ? 'underline' : 'none'}`,
    `font-style:${el.italic ? 'italic' : 'normal'}`,
    `text-align:${el.align || 'left'}`,
    `color:${el.color || '#000'}`,
    `overflow:hidden`,
    `box-sizing:border-box`,
    `line-height:1.15`,
    // #285 Sarma kapalıysa tek satır (tasarımcıdaki `whiteSpace:'nowrap'` ile birebir);
    // böylece uzun ünvan alt satıra taşıp altındaki alanları sıkıştırmaz.
    `white-space:${wrap ? 'normal' : 'nowrap'}`,
  ];
  if (el.bgColor) parts.push(`background:${el.bgColor}`);
  if (el.border) parts.push(`border:${el.borderWidth || 0.3}mm solid ${el.borderColor || '#000'}`, 'padding:0.5mm 1mm');
  if (el.radius) parts.push(`border-radius:${el.radius}mm`);
  if (wrap) parts.push('overflow-wrap:break-word', 'word-break:break-word');
  if (el.opacity != null && el.opacity < 1) parts.push(`opacity:${el.opacity}`);
  if (el.valign === 'middle') parts.push('display:flex', 'align-items:center');
  if (el.valign === 'bottom') parts.push('display:flex', 'align-items:flex-end');
  return parts.join(';');
}

// Tek bir öğeyi verilen `top` (mm) konumunda mutlak konumlu HTML'e çevirir.
// `top` çağrı yerine göre ya sayfa-mutlak (el.y) ya da akan footer sarmalayıcısına göredir.
function renderElement(el, top, data) {
  const op = el.opacity != null && el.opacity < 1 ? `opacity:${el.opacity};` : '';
  if (el.type === 'line') {
    return `<div style="position:absolute;left:${el.x}mm;top:${top}mm;width:${el.width}mm;border-top:${el.height || 0.3}mm solid ${el.color || '#000'};${op}"></div>`;
  }
  if (el.type === 'box') {
    const fill = el.bgColor ? `background:${el.bgColor};` : '';
    const rad = el.radius ? `border-radius:${el.radius}mm;` : '';
    return `<div style="position:absolute;left:${el.x}mm;top:${top}mm;width:${el.width}mm;height:${el.height}mm;border:${el.borderWidth || 0.3}mm solid ${el.borderColor || '#000'};${fill}${rad}${op}box-sizing:border-box"></div>`;
  }
  if (el.type === 'image') {
    const rad = el.radius ? `border-radius:${el.radius}mm;overflow:hidden;` : '';
    const brd = el.border ? `border:${el.borderWidth || 0.3}mm solid ${el.borderColor || '#000'};` : '';
    const inner = el.src ? `<img src="${escapeHtml(el.src)}" style="width:100%;height:100%;object-fit:${el.fit || 'contain'};display:block"/>` : '';
    return `<div style="position:absolute;left:${el.x}mm;top:${top}mm;width:${el.width}mm;height:${el.height}mm;${brd}${rad}${op}box-sizing:border-box">${inner}</div>`;
  }
  if (el.type === 'barcode') {
    const inner = barcodeHtml(resolveBarcodeValue(el, data), el.format, el.showText);
    return `<div style="position:absolute;left:${el.x}mm;top:${top}mm;width:${el.width}mm;height:${el.height}mm;${op}box-sizing:border-box">${inner}</div>`;
  }
  const val = resolveElementValue(el, data);
  return `<div style="${elementStyle(el, top)}">${escapeHtml(val)}</div>`;
}

// layout + data → tek sayfanın iç HTML'i (printable alan içeriği).
function renderBody(layout, data) {
  const m = layout.page.margins || { top: 0, right: 0, bottom: 0, left: 0 };
  // Gizli (hidden) öğeler baskıda çizilmez — tasarımcıda gizlenen alan çıktıda da görünmemeli.
  const elements = (layout.elements || []).filter((e) => !e.hidden);
  const footerEls = ((layout.footer && layout.footer.elements) || []).filter((e) => !e.hidden);
  const band = layout.detailBand;
  const hasBand = !!(band && band.enabled && (band.columns || []).length);
  const rows = hasBand ? ((data && data.detail) || []) : [];

  // Tasarım ekranı detay bandını sabit 2 satır (≥12mm) gösterir; baskıda band tüm kalemleri
  // içerecek kadar uzar (ürün adları sarınca satırlar 2-3 satıra çıkar — yükseklik önceden
  // kestirilemez). Bu yüzden bandın altındaki öğeleri OTOMATİK sınıflarız:
  //  • Bandın altında ama sayfa dibine sabitlenmemiş öğeler (dip toplam, ara toplam, üstündeki
  //    çizgi) → tablodan hemen SONRA normal akışta çizilir; kaç satır olursa olsun çakışmaz.
  //  • Sayfanın alt kenarına yapışık öğeler (imza, sayfa-sonu çizgisi) → tasarımdaki sabit
  //    konumda kalır (akmaz), böylece sayfa dibinde durur.
  //  • Bandın üstündeki öğeler (başlık alanı) → her zaman sabit.
  // (`followDetail` açıkça verilmişse otomatik karar yerine onu uygularız — manuel override.)
  const { heightMm } = paperDims(layout.page.paperSize, layout.page.orientation, layout.page.widthMm, layout.page.heightMm);
  const isThermal = String(layout.page.paperSize || '').startsWith('Thermal');
  const printH = heightMm - m.top - m.bottom;
  const PIN_ZONE = 12; // mm: printable alt kenarına bu kadar yakın öğeler sayfa-sabit sayılır
  const rowHeight = band?.rowHeight || 7;
  const headerH = band?.showHeader ? (band?.headerHeight || rowHeight) : 0;
  const designedBandBottom = hasBand ? band.y + Math.max(rowHeight * 2, 12) : Infinity;

  // Termal ruloda "sayfa dibi" kavramı yoktur (boy içerikle uzar) → diptekiler de akar.
  const isPagePinned = (el) => !isThermal && (el.y + (el.height || 0)) >= (printH - PIN_ZONE);
  const isFooter = (el) => {
    if (!hasBand) return false;
    if (el.followDetail === true) return true;        // manuel override: zorla akıt
    if (el.followDetail === false) return false;      // manuel override: zorla sabitle
    return el.y >= designedBandBottom - 0.5 && !isPagePinned(el); // otomatik
  };

  const all = [...elements, ...footerEls];
  const aboveEls = all.filter((el) => !isFooter(el));   // sabit/mutlak (başlık + sayfa dibi)
  const flowEls = all.filter(isFooter);                 // bandı takip eden akan öğeler

  const aboveHtml = aboveEls.map((el) => renderElement(el, el.y, data)).join('\n');

  let bandGroupHtml = '';
  if (hasBand) {
    // Kolonların toplamı band genişliğini aşarsa orantılı küçült (tasarımcı önizlemesi kolonları
    // hep band'e oranlar; print ise ham mm kullanıp tabloyu taşırıyordu → ayrım/çizgi "yarım" görünüyordu).
    const colTotal = band.columns.reduce((a, c) => a + (c.width || 0), 0);
    const colScale = colTotal > band.width && colTotal > 0 ? band.width / colTotal : 1;
    const cw = (c) => (c.width || 0) * colScale;
    const headerBg = band.headerBgColor ? `background:${band.headerBgColor};` : '';
    const tblBorder = band.borderColor ? `border:${band.borderWidth || 0.3}mm solid ${band.borderColor};` : '';
    const colHeader = band.showHeader
      ? `<tr>${band.columns.map((c) => `<th style="height:${headerH}mm;width:${cw(c)}mm;text-align:${c.align || 'left'};font-size:${c.fontSizePt || 9}pt;${headerBg}border-bottom:0.3mm solid #000;padding:0.5mm 1mm;box-sizing:border-box;overflow-wrap:break-word;word-break:break-word">${escapeHtml(c.headerText || c.fieldKey)}</th>`).join('')}</tr>`
      : '';
    const sampleRows = rows.length ? rows : [null]; // veri yoksa bir placeholder satır
    const body = sampleRows.map((row) => `<tr>${band.columns.map((c) => {
      let txt;
      if (row) txt = formatValue(row[c.fieldKey], c.fieldType || c.format);
      else txt = `[${c.fieldKey}]`;
      return `<td style="width:${cw(c)}mm;text-align:${c.align || 'left'};font-size:${c.fontSizePt || 9}pt;font-weight:${c.bold ? 'bold' : 'normal'};text-decoration:${c.underline ? 'underline' : 'none'};color:${c.color || '#000'};padding:0.5mm 1mm;box-sizing:border-box;vertical-align:top;overflow-wrap:break-word;word-break:break-word">${escapeHtml(txt || '')}</td>`;
    }).join('')}</tr>`).join('\n');

    // Footer toplam satırı: kolonun "summary" tipine göre toplam/say/ortalama; aksi halde footerText.
    let colFooter = '';
    if (band.showFooter && band.columns.some((c) => (c.summary && c.summary !== 'none') || c.footerText)) {
      colFooter = `<tr>${band.columns.map((c) => {
        let txt = '';
        if (c.summary && c.summary !== 'none') {
          const v = rows.length ? computeAggregate(rows, c.fieldKey, c.summary)
            : (c.summary === 'count' ? 0 : null);
          txt = v !== null && v !== undefined ? formatValue(v, c.fieldType || c.format || (c.summary === 'count' ? 'number' : 'money')) : '';
        } else if (c.footerText) {
          txt = c.footerText;
        }
        return `<td style="width:${cw(c)}mm;text-align:${c.align || 'left'};font-size:${c.fontSizePt || 9}pt;font-weight:bold;color:${c.color || '#000'};border-top:0.3mm solid #000;padding:0.5mm 1mm;box-sizing:border-box;vertical-align:top;overflow-wrap:break-word;word-break:break-word">${escapeHtml(txt || '')}</td>`;
      }).join('')}</tr>`;
    }

    if (flowEls.length) {
      // Band + akan footer'ı tek bir mutlak konumlu grupta tut: tablo akışta, footer tablodan sonra.
      const footerMinY = Math.min(...flowEls.map((e) => e.y));
      // band.y'den footer'ın TASARIM konumuna kadarki alanı rezerve et. Tablo bu alandan kısaysa
      // footer tasarımdaki yerinde kalır (WYSIWYG — yukarı çekilmez); tablo daha uzunsa footer
      // gerçek satırların altına akar (çakışmaz). x ise her zaman tasarımdaki gibi korunur.
      const reserve = Math.max(0, footerMinY - band.y);
      const footerH = Math.max(...flowEls.map((e) => (e.y - footerMinY) + (e.height || 0))) + 2;
      const flowHtml = flowEls.map((el) => renderElement(el, el.y - footerMinY, data)).join('\n');
      const tableWrap = `<div style="min-height:${reserve}mm"><table style="margin-left:${band.x}mm;width:${band.width}mm;border-collapse:collapse;table-layout:fixed;${tblBorder}">${colHeader}${body}${colFooter}</table></div>`;
      const footerWrap = `<div style="position:relative;height:${footerH}mm">${flowHtml}</div>`;
      bandGroupHtml = `<div style="position:absolute;left:0;right:0;top:${band.y}mm">${tableWrap}${footerWrap}</div>`;
    } else {
      bandGroupHtml = `<table style="position:absolute;left:${band.x}mm;top:${band.y}mm;width:${band.width}mm;border-collapse:collapse;table-layout:fixed;${tblBorder}">${colHeader}${body}${colFooter}</table>`;
    }
  }

  return `<div style="position:absolute;left:${m.left}mm;top:${m.top}mm;right:${m.right}mm;bottom:${m.bottom}mm">${aboveHtml}\n${bandGroupHtml}</div>`;
}

export function buildPrintHtml(layout, data, title = 'Belge') {
  const { widthMm, heightMm } = paperDims(
    layout.page.paperSize, layout.page.orientation,
    layout.page.widthMm, layout.page.heightMm,
  );
  const isThermal = String(layout.page.paperSize || '').startsWith('Thermal');
  const landscape = layout.page.orientation === 1;
  // @page boyutu:
  //  • Termal: sabit genişlik, otomatik yükseklik (rulo).
  //  • Bilinen kağıt (A4/A5/Letter): ADLI boyut + portrait/landscape anahtarı. Özel mm boyutu
  //    (örn. "297mm 210mm") çoğu yazıcı sürücüsünde dikey A4'e indirgenip yatay içerik kırpıldığı
  //    için ("yatayda yarım baskı"), adlı boyut + landscape ile sürücü fiziksel sayfayı doğru döndürür.
  //  • Özel boyut: mm × mm.
  const NAMED_PAGE = { A4: 'A4', A5: 'A5', Letter: 'letter' };
  const named = NAMED_PAGE[layout.page.paperSize];
  let pageSize;
  if (isThermal) pageSize = `${widthMm}mm auto`;
  else if (named) pageSize = `${named} ${landscape ? 'landscape' : 'portrait'}`;
  else pageSize = `${widthMm}mm ${heightMm}mm`;

  return `<!doctype html>
<html lang="tr"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  @page { size: ${pageSize}; margin: 0; }
  html, body { margin: 0; padding: 0; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page {
    position: relative;
    width: ${widthMm}mm;
    ${isThermal ? '' : `height: ${heightMm}mm;`}
    font-family: 'Courier New', monospace;
    color: #000;
    background: #fff;
  }
  @media screen {
    body { background: #525659; padding: 16px; }
    .page { box-shadow: 0 0 8px rgba(0,0,0,0.4); margin: 0 auto; }
  }
</style></head>
<body onload="window.focus()">
  <div class="page">${renderBody(layout, data)}</div>
</body></html>`;
}

// Yazdırma penceresini açar ve baskıyı tetikler. orientationCss not: @page yön + size kullanılır.
export function printLayout(layout, data, title = 'Belge') {
  const html = buildPrintHtml(layout, data, title);
  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) { alert('Yazdırma penceresi engellendi. Lütfen pop-up iznini açın.'); return; }
  win.document.open();
  win.document.write(html);
  win.document.close();
  // İçerik yerleşsin diye küçük gecikme.
  setTimeout(() => { try { win.print(); } catch (_) { /* kullanıcı kapatmış olabilir */ } }, 300);
}
